// apps/web/src/components/layout/brand-selector.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useBrands, type BrandListItem } from "@/hooks/use-brands";
import { CreateBrandDialog } from "@/components/brand/CreateBrandDialog";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "react-i18next";

interface BrandSelectorProps {
  currentSlug?: string;
}

const NEW_BRAND_SENTINEL = "__new__";

export function BrandSelector({ currentSlug }: BrandSelectorProps) {
  const navigate = useNavigate();
  const { data: brands, isLoading } = useBrands();
  const { user } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const { t } = useTranslation("common");

  if (isLoading || !brands?.length) {
    return <div className="h-9 rounded-md bg-muted animate-pulse" />;
  }

  function handleChange(value: string) {
    if (value === NEW_BRAND_SENTINEL) {
      setCreateOpen(true);
      return;
    }
    navigate(`/${value}`);
  }

  return (
    <>
      <Select value={currentSlug} onValueChange={handleChange}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t("selectBrand")} />
        </SelectTrigger>
        <SelectContent>
          {brands.map((brand: BrandListItem) => (
            <SelectItem key={brand.id} value={brand.slug}>
              {brand.name}
            </SelectItem>
          ))}
          {user?.isSuperAdmin && (
            <SelectItem value={NEW_BRAND_SENTINEL} className="text-primary">
              <span className="flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                {t("newBrand")}
              </span>
            </SelectItem>
          )}
        </SelectContent>
      </Select>
      <CreateBrandDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
}
